/**
 * ShareLinksManager — lets a PM generate, view, and revoke external share links for a project.
 */
import { useState, useEffect } from 'react'
import { Link2, Copy, Trash2, Plus, EyeOff, Clock, CheckCircle } from 'lucide-react'
import { shareLinksService, type ShareLink, type VisibilityConfig } from '../services/shareLinks'
import { toast } from 'sonner'
import { DateDisplay } from './ui/DateDisplay'

interface Props {
  projectId: number
}

const defaultVisibility: VisibilityConfig = {
  show_status: true,
  show_milestones: true,
  show_budget_summary: false,
  show_flagged_risks: false,
}

export function ShareLinksManager({ projectId }: Props) {
  const [links, setLinks] = useState<ShareLink[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    label: '',
    password: '',
    expires_at: '',
    visibility_config: { ...defaultVisibility },
  })

  useEffect(() => { loadLinks() }, [projectId])

  const loadLinks = async () => {
    setLoading(true)
    try {
      const data = await shareLinksService.list(projectId)
      setLinks(data)
    } catch {
      toast.error('Failed to load share links')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    try {
      const link = await shareLinksService.create(projectId, {
        label: form.label || undefined,
        visibility_config: form.visibility_config,
        password: form.password || undefined,
        expires_at: form.expires_at || undefined,
      })
      setLinks(prev => [link, ...prev])
      setShowCreate(false)
      setForm({ label: '', password: '', expires_at: '', visibility_config: { ...defaultVisibility } })
      toast.success('Share link created')
    } catch {
      toast.error('Failed to create share link')
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (linkId: number) => {
    if (!confirm('Revoke this link? Anyone with the URL will lose access immediately.')) return
    try {
      await shareLinksService.revoke(projectId, linkId)
      setLinks(prev => prev.filter(l => l.id !== linkId))
      toast.success('Link revoked')
    } catch {
      toast.error('Failed to revoke link')
    }
  }

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard')
  }

  const toggleVisibility = (key: keyof VisibilityConfig) => {
    setForm(f => ({
      ...f,
      visibility_config: { ...f.visibility_config, [key]: !f.visibility_config[key] },
    }))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-neutral-950">External Share Links</h3>
        <button
          onClick={() => setShowCreate(v => !v)}
          className="flex items-center gap-2 px-3 py-2 bg-[#486581] text-white text-sm rounded-lg hover:bg-[#3d5570] transition-colors min-h-[40px]"
          aria-expanded={showCreate}
        >
          <Plus className="w-4 h-4" aria-hidden="true" />
          Create Link
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4"
          aria-label="Create share link"
        >
          <div>
            <label htmlFor="link-label" className="block text-xs font-medium text-gray-700 mb-1">
              Label <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="link-label"
              type="text"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="e.g. Client review — Q2"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
            />
          </div>

          {/* Visibility toggles */}
          <fieldset>
            <legend className="text-xs font-medium text-gray-700 mb-2">What to show externally</legend>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(defaultVisibility) as (keyof VisibilityConfig)[]).map(key => (
                <label
                  key={key}
                  className="flex items-center gap-2 text-sm cursor-pointer min-h-[40px] px-3 py-2 border border-gray-200 rounded-lg hover:bg-white transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={form.visibility_config[key]}
                    onChange={() => toggleVisibility(key)}
                    className="rounded"
                  />
                  <span className="text-gray-700">
                    {key.replace('show_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="link-password" className="block text-xs font-medium text-gray-700 mb-1">
                Password <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="link-password"
                type="password"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Leave blank for no password"
                autoComplete="new-password"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              />
            </div>
            <div>
              <label htmlFor="link-expires" className="block text-xs font-medium text-gray-700 mb-1">
                Expiry date <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                id="link-expires"
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[40px]"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 bg-[#486581] text-white text-sm rounded-lg hover:bg-[#3d5570] disabled:opacity-50 transition-colors min-h-[40px]"
            >
              {creating ? 'Creating…' : 'Create link'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors min-h-[40px]"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Links list */}
      {loading ? (
        <p className="text-sm text-gray-500 text-center py-4">Loading…</p>
      ) : links.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-200 rounded-xl">
          <Link2 className="w-8 h-8 text-gray-300 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm text-gray-500">No share links yet</p>
        </div>
      ) : (
        <ul className="space-y-3" role="list" aria-label="Share links">
          {links.map(link => (
            <li key={link.id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-medium text-neutral-950">{link.label || 'Untitled link'}</span>
                    {!link.is_active && (
                      <span className="text-[11px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded border border-red-200">Revoked</span>
                    )}
                    {link.has_password && (
                      <span className="text-[11px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 flex items-center gap-1">
                        <EyeOff className="w-2.5 h-2.5" aria-hidden="true" /> Password protected
                      </span>
                    )}
                    {link.expires_at && (
                      <span className="text-[11px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" aria-hidden="true" />
                        Expires <DateDisplay date={link.expires_at} />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate font-mono">{link.share_url}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {link.access_count} view{link.access_count !== 1 ? 's' : ''}
                    {link.last_accessed_at && (
                      <> · Last accessed <DateDisplay date={link.last_accessed_at} /></>
                    )}
                    {' · '}Created <DateDisplay date={link.created_at} />
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleCopy(link.share_url)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                    aria-label="Copy share URL"
                  >
                    <Copy className="w-4 h-4 text-gray-500" aria-hidden="true" />
                  </button>
                  {link.is_active && (
                    <button
                      onClick={() => handleRevoke(link.id)}
                      className="p-2 hover:bg-red-50 rounded-lg transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                      aria-label="Revoke link"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              {/* Visibility chips */}
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {Object.entries(link.visibility_config).map(([k, v]) => v ? (
                  <span key={k} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full">
                    <CheckCircle className="w-2.5 h-2.5" aria-hidden="true" />
                    {k.replace('show_', '').replace(/_/g, ' ')}
                  </span>
                ) : null)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
